import React, { useEffect, useState, useCallback, useRef} from 'react';
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../config.js";
import { toast } from "react-toastify";
import styles from './Notifications.module.css';
import { useSelector, useDispatch } from 'react-redux';
import { setNotificationCount } from '../../reducer';

const Notifications = (ComponentToRenderData) => {
    const navigate = useNavigate();
    const params = useParams();
    const SystemUrl = ComponentToRenderData.SystemUrl;
    const token = localStorage.getItem('token')
    const search = window.location.search;
    let URLParams = new URLSearchParams(search);
    const dispatch = useDispatch();
    const unreadCount = useSelector((state) => state.notification?.count ?? 0);

    const [data, setData] = useState([]);
    const [countsData, setCountsData] = useState({});
    const [iconsData, setIconsData] = useState({});
    const [TotalData, setTotalData] = useState({});
    const [loading, setLoading] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, right: 'auto' });
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    const routeChange = path => {
        navigate(path);
    }

    // Handle clicking outside dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
                setExpandedGroup(null);
            }
        };

        const handleResize = () => {
            if (isDropdownOpen) {
                calculateDropdownPosition();
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('resize', handleResize);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [isDropdownOpen]);

    const calculateDropdownPosition = () => {
        if (!buttonRef.current) return;

        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const dropdownWidth = 320;
        const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl';
        
        let position = {
            top: buttonRect.bottom + 8,
            left: 'auto',
            right: 'auto'
        };

        if (isRTL) {
            // For RTL (Arabic), align dropdown to the left edge of button
            const leftPosition = buttonRect.left;
            if (leftPosition + dropdownWidth > viewportWidth) {
                // If dropdown would overflow viewport, align to right edge instead
                position.right = viewportWidth - buttonRect.right;
            } else {
                position.left = leftPosition;
            }
        } else {
            // For LTR, align dropdown to the right edge of button
            const rightPosition = buttonRect.right;
            if (rightPosition - dropdownWidth < 0) {
                // If dropdown would overflow viewport, align to left edge instead
                position.left = buttonRect.left;
            } else {
                position.right = viewportWidth - rightPosition;
            }
        }

        setDropdownPosition(position);
    };

    const getNotification = useCallback(() => {
        setLoading(true);
        axios(config.SystemUrl + `Notification`, {
            headers: {
                "Accept": "application/json",
                "lang": "en",
                'Authorization': "Bearer " + token
            }
        })
            .then(result => {
                if (result.data && result.data.hasOwnProperty('data')) {
                    setData(Object.entries(result.data.data));
                    setCountsData(result.data['counts'] || {});
                    setIconsData(result.data['icons'] || {});
                    setTotalData(result.data['total']);
                    if (typeof result.data['total'] === 'number') {
                        dispatch(setNotificationCount(result.data['total']));
                    }
                }
            })
            .catch(err => {
                console.error('[Notifications] Failed to load notifications:', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [token, dispatch]);

    const toggleDropdown = (e) => {
        e.preventDefault();
        if (!isDropdownOpen) {
            calculateDropdownPosition();
            setExpandedGroup(null); // Close any expanded groups when opening dropdown
            getNotification(); // Fetch notification list on demand when opened
        }
        setIsDropdownOpen(!isDropdownOpen);
    };

    const toggleNotificationGroup = (groupIndex, event) => {
        event.stopPropagation(); // Prevent dropdown from closing
        setExpandedGroup(expandedGroup === groupIndex ? null : groupIndex);
    };

    const handleNotificationClick = (notification) => {
        // Mark as read if unread
        if (notification.is_read === 0) {
            axios.get(config.SystemUrl + `MarkAsRead?id=${notification.id}`)
                .then(result => {
                    getNotification();
                })
                .catch(error => {
                    console.error('Error marking notification as read:', error);
                });
        }
        // Close dropdown when navigating
        setIsDropdownOpen(false);
    };

    return (
        <>
            <div ref={dropdownRef}>
                {/* Bell Icon Button */}
                <button 
                    ref={buttonRef}
                    type="button"
                    onClick={toggleDropdown} 
                    className={`${styles.bellButton} ${isDropdownOpen ? styles.bellButtonActive : ''}`}
                    aria-label="Notifications"
                >
                    <i className={`bi bi-bell ${styles.bellIcon}`}></i>
                    
                    {/* Notification Badge */}
                    {unreadCount > 0 ? (
                        <span className={styles.badgeCount}>
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    ) : null}
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                    <div 
                        className="position-fixed shadow-lg bg-white rounded-3 border"
                        style={{
                            top: `${dropdownPosition.top}px`,
                            left: dropdownPosition.left !== 'auto' ? `${dropdownPosition.left}px` : 'auto',
                            right: dropdownPosition.right !== 'auto' ? `${dropdownPosition.right}px` : 'auto',
                            width: '320px',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            zIndex: 1050
                        }}
                    >
                        {/* Arrow Pointer */}
                        <div 
                            className="position-absolute"
                            style={{
                                top: '-8px',
                                right: dropdownPosition.right !== 'auto' ? '20px' : 'auto',
                                left: dropdownPosition.left !== 'auto' ? '20px' : 'auto',
                                width: '0',
                                height: '0',
                                borderLeft: '8px solid transparent',
                                borderRight: '8px solid transparent',
                                borderBottom: '8px solid white',
                                filter: 'drop-shadow(0 -2px 4px rgba(0,0,0,0.1))'
                            }}
                        ></div>

                        {/* Header */}
                        <div className="px-3 py-3 border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold">Notifications</h6>
                                {unreadCount > 0 && (
                                    <span className="badge bg-primary rounded-pill">{unreadCount}</span>
                                )}
                            </div>
                        </div>

                        {/* Notification Content */}
                        <div className={styles.notificationContent} style={{ maxHeight: '320px', overflowY: 'auto' }}>
                            {loading && data.length === 0 ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="text-muted mt-2 mb-0 small">Loading notifications...</p>
                                </div>
                            ) : data.length === 0 ? (
                                <div className="text-center py-4">
                                    <i className="bi bi-bell-slash text-muted" style={{ fontSize: '2rem' }}></i>
                                    <p className="text-muted mt-2 mb-0">No notifications</p>
                                </div>
                            ) : (
                                data.map((notifications, index) => (
                                    <div key={index} className="notification-group">
                                        {/* Group Header */}
                                        <div 
                                            className={`${styles.notificationGroupHeader} px-3 py-2 bg-light border-bottom cursor-pointer`}
                                            onClick={(e) => toggleNotificationGroup(index, e)}
                                            style={{ 
                                              cursor: 'pointer',
                                              backgroundColor: expandedGroup === index ? '#f8f9fa' : '#ffffff'
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center">
                                                    <i className={`${iconsData[notifications[0]]} me-2 text-primary`}></i>
                                                    <span className="fw-semibold">{notifications[0]}</span>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <span className="badge bg-secondary rounded-pill me-2">
                                                        {countsData[notifications[0]]}
                                                    </span>
                                                    <i className={`bi bi-chevron-${expandedGroup === index ? 'up' : 'down'} text-muted`}></i>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Group Items */}
                                        {expandedGroup === index && (
                                            <div className="notification-list">
                                                {notifications[1].map((notification, notifIndex) => (
                                                    <div 
                                                        key={notifIndex}
                                                        className={`${styles.notificationItem} px-3 py-2 border-bottom`}
                                                        style={{ 
                                                            backgroundColor: notification.is_read === 0 ? '#f8f9ff' : 'transparent',
                                                            borderLeft: notification.is_read === 0 ? '3px solid #007bff' : '3px solid transparent'
                                                        }}
                                                        onClick={() => handleNotificationClick(notification)}
                                                    >
                                                        <Link 
                                                            to={'/' + notification.url}
                                                            className="text-decoration-none"
                                                        >
                                                            <div className="d-flex">
                                                                {notification.is_read === 0 && (
                                                                    <div className="me-2 mt-1">
                                                                        <div 
                                                                            className="bg-primary rounded-circle"
                                                                            style={{ width: '6px', height: '6px' }}
                                                                        ></div>
                                                                    </div>
                                                                )}
                                                                <div className="flex-grow-1">
                                                                    <p className="mb-1 text-dark small" style={{ lineHeight: '1.4' }}>
                                                                        {notification.text}
                                                                    </p>
                                                                    <small className="text-muted">
                                                                        {notification.date}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );

}
export default Notifications;

